import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticlesByCategory, getHotArticles } from '@/mocks/articles';
import { getCategoryBySlug, categories } from '@/mocks/categories';

type SortType = 'latest' | 'popular';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];
  const hotArticles = getHotArticles(3);
  const [sortBy, setSortBy] = useState<SortType>('latest');

  const sortedArticles = useMemo(() => {
    const articles = [...categoryArticles];
    if (sortBy === 'latest') {
      return articles.sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    }
    return articles.sort((a, b) => b.views - a.views);
  }, [categoryArticles, sortBy]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="container-main text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name}`}
        description={category.description}
        keywords={`黑料社区,${category.name},黑料爆料,独家内幕,真实爆料`}
        canonicalUrl={`https://heiliaoshequ.com/category/${slug}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: `黑料社区 - ${category.name}`,
        url: `https://heiliaoshequ.com/category/${slug}`,
        description: category.description,
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="container-main py-6">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: category.name },
            ]} />
          </div>

          <section className="pb-14">
            <div className="container-main">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                      category.color === 'primary' ? 'bg-primary-100 text-primary-500' :
                      category.color === 'accent' ? 'bg-accent-100 text-accent-500' :
                      'bg-secondary-100 text-secondary-500'
                    }`}>
                      <i className={`${category.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{category.name}</h1>
                      <p className="text-xs text-foreground-500">{category.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <span className="text-sm text-foreground-500">共 {categoryArticles.length} 篇爆料</span>
                    <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                      {[
                        { value: 'latest' as SortType, label: '最新' },
                        { value: 'popular' as SortType, label: '最热' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setSortBy(opt.value)}
                          className={`px-4 py-1.5 text-xs rounded-full transition-colors duration-200 whitespace-nowrap ${
                            sortBy === opt.value
                              ? 'bg-primary-500 text-white'
                              : 'text-foreground-600 hover:text-foreground-900'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {sortedArticles.length === 0 ? (
                    <div className="text-center py-20">
                      <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                      <p className="mt-4 text-foreground-500">该分类暂无文章</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedArticles.map((article) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/60 transition-colors duration-200"
                        >
                          <div className="shrink-0 w-full sm:w-48 h-36 rounded-lg overflow-hidden">
                            <img
                              src={article.imageCard}
                              alt={article.title}
                              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {article.isExclusive && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">
                                  独家
                                </span>
                              )}
                              {article.isHot && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-medium">
                                  热门
                                </span>
                              )}
                              <span className="text-[10px] text-foreground-400">{article.publishDate}</span>
                            </div>
                            <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                              {article.title}
                            </h3>
                            <p className="mt-1.5 text-sm text-foreground-500 line-clamp-2">{article.summary}</p>
                            <div className="flex items-center gap-4 mt-3 text-[10px] text-foreground-400">
                              <span className="flex items-center gap-0.5">
                                {article.authorVerified && <i className="ri-verified-badge-fill text-primary-400"></i>}
                                {article.author}
                              </span>
                              <span><i className="ri-eye-line mr-0.5"></i>{article.views.toLocaleString()} 阅读</span>
                              <span><i className="ri-chat-3-line mr-0.5"></i>{article.comments} 评论</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <aside className="w-full lg:w-80 shrink-0">
                  <div className="lg:sticky lg:top-24 space-y-6">
                    <div className="rounded-lg bg-background-100 p-5">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">全部分类</h4>
                      <div className="space-y-1">
                        {categories.map((cat) => (
                          <Link
                            key={cat.slug}
                            to={`/category/${cat.slug}`}
                            className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                              cat.slug === slug
                                ? 'bg-primary-100/70 text-primary-700 font-medium'
                                : 'text-foreground-600 hover:bg-background-200/50'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <i className={`${cat.icon} text-xs`}></i>
                              {cat.name}
                            </span>
                            <span className="text-xs text-foreground-400">{cat.count}</span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-lg bg-background-100 p-5">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-4">热门爆料</h4>
                      <div className="space-y-3">
                        {hotArticles.map((article, idx) => (
                          <Link
                            key={article.id}
                            to={`/article/${article.id}`}
                            className="flex gap-3 group"
                          >
                            <span className={`shrink-0 w-6 h-6 flex items-center justify-center rounded text-xs font-bold ${
                              idx === 0 ? 'bg-accent-500 text-white' :
                              idx === 1 ? 'bg-primary-500 text-white' :
                              'bg-secondary-500 text-white'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="text-xs text-foreground-700 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 leading-snug">
                              {article.title}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}